import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import defaultLogo from 'src/assets/images/logos/logo-tclc.png';

const COMPANY_CODE_STORAGE_KEY = 'quotly_company_code';
const DEFAULT_COMPANY_NAME = 'TCLC AI Services Private Limited';
const DEFAULT_BRANDING = { companyName: '', companyLogo: '' };

let cachedBrandingKey = '';
let cachedBrandingValue = null;
let cachedBrandingPromise = null;

const getPreferredCompanyCode = () => {
  if (typeof window === 'undefined') return 'TCLC';
  return window.localStorage.getItem(COMPANY_CODE_STORAGE_KEY) || 'TCLC';
};

export const useCompanyBranding = () => {
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');

  const loadConfig = useCallback(async () => {
    const companyCode = getPreferredCompanyCode();
    const tenantId = typeof window !== 'undefined' ? window.localStorage.getItem('tenantID') : '';
    const cacheKey = JSON.stringify({
      tenantId: tenantId || '',
      companyCode: companyCode || '',
    });

    const applyBranding = (branding) => {
      setCompanyName(branding?.companyName || '');
      setCompanyLogo(branding?.companyLogo || '');
    };

    if (cachedBrandingKey === cacheKey && cachedBrandingValue) {
      applyBranding(cachedBrandingValue);
      return;
    }

    try {
      if (cachedBrandingKey !== cacheKey || !cachedBrandingPromise) {
        cachedBrandingKey = cacheKey;
        cachedBrandingPromise = axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/config`, {
          params: tenantId
            ? { tenant_id: tenantId, ...(companyCode ? { company_code: companyCode } : {}) }
            : companyCode
              ? { company_code: companyCode }
              : undefined,
        }).then(({ data }) => {
          cachedBrandingValue = {
            companyName: data?.companyName || '',
            companyLogo: data?.companyLogo?.url || '',
          };
          return cachedBrandingValue;
        }).catch((error) => {
          cachedBrandingKey = '';
          cachedBrandingValue = null;
          throw error;
        }).finally(() => {
          cachedBrandingPromise = null;
        });
      }

      applyBranding(await cachedBrandingPromise);
    } catch {
      applyBranding(DEFAULT_BRANDING);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    const refresh = () => {
      cachedBrandingKey = '';
      cachedBrandingValue = null;
      cachedBrandingPromise = null;
      loadConfig();
    };
    window.addEventListener('company-config-updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('company-config-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [loadConfig]);

  return useMemo(() => ({
    companyName: companyName || DEFAULT_COMPANY_NAME,
    companyLogoUrl: companyLogo || defaultLogo,
    hasCustomCompanyLogo: Boolean(companyLogo),
  }), [companyLogo, companyName]);
};

export default useCompanyBranding;
